import { revokeCurrentStaffSession } from '../modules/auth/staffSession';
import { Link, useNavigate } from 'react-router-dom';
import styled, { css, useTheme } from 'styled-components';
import { TurnOffIcon } from '../assets/icons/turnOff';
import brandConfig from '../config/brand';
import { useAppDispatch } from '../store';
import { clearToken } from '../modules/auth/state/authSlice';
import { api } from '../api/api';
import { NavOptionType } from './Layout';
import { NewBadge } from './NewBadge';

type SideNavProps = {
  navOptions: NavOptionType[];
};

export const SideNav = ({ navOptions }: SideNavProps) => {
  const { colors } = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const onLogoutPress = async () => {
    await revokeCurrentStaffSession();
    dispatch(clearToken());
    dispatch(api.util.resetApiState());
    navigate('/');
  };

  return (
    <Container>
      <TopWrapper>
        <Logo src={brandConfig.logo} alt={`${brandConfig.displayName} logo`} />
        {navOptions.map((navOption) => (
          <NavItemWrapper key={navOption.to}>
            <StyledLink
              to={navOption.to}
              isActive={Boolean(navOption.isSelected)}
              target={navOption.isExternal ? '_blank' : undefined}>
              {navOption.icon}
              <Label isActive={Boolean(navOption.isSelected)}>{navOption.name}</Label>
              {navOption.isNew && <NewBadge size="small" />}
            </StyledLink>
          </NavItemWrapper>
        ))}
      </TopWrapper>
      <LogoutPress onClick={onLogoutPress}>
        <TurnOffIcon size={30} color={colors.red} />
        <LogoutText>Logout</LogoutText>
      </LogoutPress>
    </Container>
  );
};

const TopWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const NavItemWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const Logo = styled.img`
  width: 5rem;
  margin-bottom: 5rem;
`;

export const LogoutText = styled.span`
  font-size: 0.9rem;
`;

export const Label = styled.span<{ isActive?: boolean }>`
  font-size: 0.9rem;
  color: ${({ isActive, theme }) => (isActive ? theme.colors.white : theme.colors.black)};
`;

const ShareStyle = css`
  align-self: flex-start;
  width: 100%;
  padding: 0.5rem;
  border-radius: 0.5rem;
  padding-left: 2rem;
  padding-right: 2rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

export const LogoutPress = styled.button`
  color: ${({ theme }) => theme.colors.red};
  ${ShareStyle}
`;
const Container = styled.div`
  padding: 0.5rem;
  border: 1px solid ${({ theme }) => theme.colors.gray};
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 0.5rem;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.gray};

  /* Keep the sidebar fixed full-height while the dashboard scrolls on desktop */
  position: sticky;
  top: 1rem;
  align-self: flex-start;
  height: calc(100dvh - 2rem);
  overflow: hidden;

  /* Fixed width so the sidebar never reflows when dashboard content
     changes width (modals, refresh, terminated-sims toggle, etc.) */
  width: 15rem;
  flex: 0 0 15rem;

  @media (max-width: 850px) {
    display: none;
  }
`;

export const StyledLink = styled(Link)<{ isActive?: boolean }>`
  ${ShareStyle}
  background-color: ${({ isActive, theme }) => (isActive ? theme.colors.primary : 'transparent')};
  transition: 0.5s;
`;
